import React, { useState, useMemo } from 'react';
import {
  Satellite, Compass, ShieldAlert, Activity, Cpu, Sparkles, FileText,
  Download, Copy, Check, RefreshCw, Layers, Calculator, Sprout, ArrowLeft,
  ArrowRight, ExternalLink, Flame, Info, CheckCircle2, AlertTriangle,
  Crosshair, ChevronRight, BarChart3, Scale, Droplets, MapPin, Eye, Zap,
  Sliders, ShieldCheck, Database
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Language, Currency, formatPriceWithCurrency } from '../utils/i18n';

interface EcoCopernicusSoilUkfAnalyzerProps {
  language: Language;
  onBack?: () => void;
  currency?: Currency;
}

interface TestSitePreset {
  id: string;
  name: { uk: string; en: string; pl: string };
  region: { uk: string; en: string; pl: string };
  lat: number;
  lng: number;
  craterDiameter: number;
  soilTypeKey: 'chernozem' | 'podzol' | 'peat';
  preDate: string;
  postDate: string;
  ccdDelta: number;
  bsiDelta: number;
  description: { uk: string; en: string; pl: string };
}

const TEST_SITES: TestSitePreset[] = [
  {
    id: 'izyum',
    name: {
      uk: 'Ізюмський агрополігон',
      en: 'Izyum Agricultural Test Site',
      pl: 'Poligon Rolniczy Izium'
    },
    region: {
      uk: 'Харківська область',
      en: 'Kharkiv Region',
      pl: 'Obwód Charkowski'
    },
    lat: 49.208,
    lng: 37.275,
    craterDiameter: 7.4,
    soilTypeKey: 'chernozem',
    preDate: '2022-04-12',
    postDate: '2022-05-08',
    ccdDelta: 0.58,
    bsiDelta: 0.34,
    description: {
      uk: 'Масований артилерійський та авіаційний обстріл чорноземних угідь важкими снарядами 152 мм та авіабомбами ФАБ-250.',
      en: 'Heavy artillery and aerial bombardment of chernozem farmland with 152mm HE-FRAG shells and FAB-250 bombs.',
      pl: 'Ciężki ostrzał artyleryjski i lotniczy czarnoziemów pociskami 152 mm oraz bombami FAB-250.'
    }
  },
  {
    id: 'orikhiv',
    name: {
      uk: 'Оріхівський тактичний сектор',
      en: 'Orikhiv Tactical Sector',
      pl: 'Sektor Taktyczny Orichiw'
    },
    region: {
      uk: 'Запорізька область',
      en: 'Zaporizhzhia Region',
      pl: 'Obwód Zaporoski'
    },
    lat: 47.568,
    lng: 35.786,
    craterDiameter: 11.2,
    soilTypeKey: 'chernozem',
    preDate: '2023-06-02',
    postDate: '2023-07-14',
    ccdDelta: 0.69,
    bsiDelta: 0.45,
    description: {
      uk: 'Ураження керованими авіабомбами КАБ-500. Глибокі вирви викиду з порушенням материнської породи та вигорянням гумусу.',
      en: 'Impact of KAB-500 guided bombs. Deep excavation craters penetrating subsoil bedrock with severe humus calcination.',
      pl: 'Uderzenie bomb kierowanych KAB-500. Głębokie leje wyrzutu naruszające skałę macierzystą i wypalenie próchnicy.'
    }
  },
  {
    id: 'kupyansk',
    name: {
      uk: 'Куп’янський прирічковий масив',
      en: 'Kupyansk Riparian Corridor',
      pl: 'Korytarz Nadrzeczny Kupiańsk'
    },
    region: {
      uk: 'Харківська область (басейн р. Оскіл)',
      en: 'Kharkiv Region (Oskil Basin)',
      pl: 'Obwód Charkowski (Basen Oskołu)'
    },
    lat: 49.712,
    lng: 37.616,
    craterDiameter: 5.6,
    soilTypeKey: 'podzol',
    preDate: '2023-09-10',
    postDate: '2023-10-04',
    ccdDelta: 0.49,
    bsiDelta: 0.28,
    description: {
      uk: 'Регулярні удари РСЗВ "Смерч" / "Град". Забруднення солями важких металів та токсичними компонентами порохових газів.',
      en: 'Multiple MLRS BM-30 Smerch and Grad rocket strikes. Contamination with heavy metal salts and propellant residues.',
      pl: 'Uderzenia wieloprowadnicowych wyrzutni rakietowych Smiercz/Grad. Skażenie metalami ciężkimi i gazami prochowymi.'
    }
  },
  {
    id: 'chornobaivka',
    name: {
      uk: 'Чорнобаївський аеропортний вузол',
      en: 'Chornobaivka Airfield Hub',
      pl: 'Węzeł Lotniskowy Czornobajiwka'
    },
    region: {
      uk: 'Херсонська область',
      en: 'Kherson Region',
      pl: 'Obwód Chersoński'
    },
    lat: 46.674,
    lng: 32.508,
    craterDiameter: 8.8,
    soilTypeKey: 'podzol',
    preDate: '2022-03-15',
    postDate: '2022-04-20',
    ccdDelta: 0.62,
    bsiDelta: 0.38,
    description: {
      uk: 'Вторинна детонація складів боєприпасів та палива. Забруднення ґрунтів свинцем, кадмієм, ПАУ та нафтопродуктами.',
      en: 'Secondary detonations of ammunition and fuel depots. Severe soil pollution with lead, cadmium, PAHs, and petroleum.',
      pl: 'Wtórne detonacje składów amunicji i paliw. Zanieczyszczenie gleby ołowiem, kadmem, WWA i produktami naftowymi.'
    }
  }
];

const SOIL_TYPES = {
  chernozem: {
    name: {
      uk: 'Чорнозем звичайний важкосуглинковий',
      en: 'Ordinary Heavy Loam Chernozem',
      pl: 'Czarnoziem Typowy Ciężko-Glinisty'
    },
    Kv: 0.72,
    Cs: 940,
    Kp: 1.35,
    density: 1.30,
    organicMatterPct: 4.8
  },
  podzol: {
    name: {
      uk: 'Дерново-підзолистий супіщаний ґрунт',
      en: 'Sod-Podzolic Sandy Loam',
      pl: 'Gleba Darniowo-Bielicowa Piaszczysta'
    },
    Kv: 0.81,
    Cs: 820,
    Kp: 1.15,
    density: 1.45,
    organicMatterPct: 2.1
  },
  peat: {
    name: {
      uk: 'Торфовище заплавне перезволожене',
      en: 'Waterlogged Riparian Peatland',
      pl: 'Torfowisko Łęgowe Podmokłe'
    },
    Kv: 0.65,
    Cs: 1150,
    Kp: 1.50,
    density: 0.85,
    organicMatterPct: 18.5
  }
};

export default function EcoCopernicusSoilUkfAnalyzer({
  language,
  onBack,
  currency = 'UAH'
}: EcoCopernicusSoilUkfAnalyzerProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = !isUk && !isPl;

  const [activeTab, setActiveTab] = useState<'remote_sensing' | 'ukf_filter' | 'sadovsky_damage' | 'phytoremediation' | 'export_report'>('remote_sensing');
  
  // Selected preset site
  const [selectedSiteId, setSelectedSiteId] = useState<string>('izyum');
  const currentSite = useMemo(() => {
    return TEST_SITES.find(s => s.id === selectedSiteId) || TEST_SITES[0];
  }, [selectedSiteId]);

  // Parameters for Sadovsky & Order 167
  const [craterDiameter, setCraterDiameter] = useState<number>(currentSite.craterDiameter);
  const [soilTypeKey, setSoilTypeKey] = useState<'chernozem' | 'podzol' | 'peat'>(currentSite.soilTypeKey);
  const [inflationIndex, setInflationIndex] = useState<number>(1.62);

  // Sync when preset site changes
  const handleSelectSite = (site: TestSitePreset) => {
    setSelectedSiteId(site.id);
    setCraterDiameter(site.craterDiameter);
    setSoilTypeKey(site.soilTypeKey);
  };

  // UKF State Simulation
  const [ukfIteration, setUkfIteration] = useState<number>(1);
  const [isUkfAutoRunning, setIsUkfAutoRunning] = useState<boolean>(false);

  // Phytoremediation selections
  const [selectedPollutant, setSelectedPollutant] = useState<'heavy_metals' | 'pah_fuel' | 'radionuclides' | 'complex'>('heavy_metals');

  // Copy status
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Current Soil Meta
  const currentSoil = SOIL_TYPES[soilTypeKey];

  // SADOVSKY CALCULATIONS
  const craterRadius = craterDiameter / 2;
  const craterDepth = Number((craterRadius * 0.48).toFixed(2));
  const craterArea = Number((Math.PI * Math.pow(craterRadius, 2)).toFixed(2));
  const craterVolume = Number(((Math.PI / 8) * Math.pow(craterDiameter, 2) * craterDepth).toFixed(2));
  const tntEquivalentKg = Number(Math.pow(craterRadius / currentSoil.Kv, 3).toFixed(1));

  // Order 167 Environmental Damages
  // R = S * H * Cs * Kp * Kind
  const damageUah = Math.round(craterArea * craterDepth * currentSoil.Cs * currentSoil.Kp * inflationIndex);
  const eurRate = 44.5;
  const damageEur = Math.round(damageUah / eurRate);

  // Munition Identification
  const munitionClass = useMemo(() => {
    if (tntEquivalentKg < 15) {
      return {
        label: isUk ? '120/122 мм мінометна міна / легкий снаряд' : isPl ? 'Mina moździerzowa 120/122 mm' : '120/122mm Mortar Shell',
        danger: isUk ? 'Локальне пошкодження верхнього шару ґрунту (0.8–1.2 м)' : 'Local topsoil blast'
      };
    } else if (tntEquivalentKg < 55) {
      return {
        label: isUk ? '152/155 мм артилерійський осколково-фугасний снаряд' : isPl ? 'Pocisk artyleryjski 152/155 mm odłamkowo-burzący' : '152/155mm Heavy Artillery Shell (OF-25 / M107)',
        danger: isUk ? 'Ударне перемішування горизонтів, залишкова токсичність свинцю і міді' : 'Subsoil disruption, toxic lead/copper residue'
      };
    } else if (tntEquivalentKg < 180) {
      return {
        label: isUk ? 'Реактивний снаряд РСЗВ "Смерч" / "Торнадо-С" (300 мм)' : isPl ? 'Pocisk rakietowy Smiercz/Tornado-S (300 mm)' : 'Heavy MLRS Rocket (BM-30 Smerch / Tornado-S)',
        danger: isUk ? 'Високотемпературне випалювання гумусу в радіусі до 15 м' : 'Humus calcination up to 15m radius'
      };
    } else if (tntEquivalentKg < 350) {
      return {
        label: isUk ? 'Авіабомба ФАБ-250 / крилата ракета (Калібр, Х-101)' : isPl ? 'Bomba lotnicza FAB-250 / pocisk manewrujący' : 'FAB-250 Aerial Bomb / Cruise Missile Warhead',
        danger: isUk ? 'Повна стерилізація мікробіому, утворення глибокої конічної вирви' : 'Complete microbiome sterilization'
      };
    } else if (tntEquivalentKg < 650) {
      return {
        label: isUk ? 'Фугасна авіабомба ФАБ-500 / КАБ-500' : isPl ? 'Bomba burząca FAB-500 / KAB-500' : 'FAB-500 / KAB-500 Heavy Aerial Bomb',
        danger: isUk ? 'Катастрофічний викид ґрунту (>100 м³), зона ураження до 35 м' : 'Catastrophic soil ejection (>100 m³), damage zone >35m'
      };
    } else {
      return {
        label: isUk ? 'Балістична ракета "Іскандер-М" / важка авіабомба КАБ-1500' : isPl ? 'Pocisk balistyczny Iskander-M / bomba KAB-1500' : 'Iskander-M Ballistic Missile / KAB-1500 Bomb',
        danger: isUk ? 'Глибинне гідрогеологічне порушення водоносного горизонту' : 'Deep hydrogeological disruption of groundwater aquifers'
      };
    }
  }, [tntEquivalentKg, isUk, isPl]);

  // 11 SIGMA POINTS CALCULATION FOR 5D UKF FILTER
  // State: [Area, ToxConc, InSAR_loss, BSI_delta, TNT_kg]
  const L = 5;
  const alpha = 0.001;
  const beta = 2.0;
  const kappa = 0;
  const lambda = Math.pow(alpha, 2) * (L + kappa) - L;
  const gamma = Math.sqrt(L + lambda);

  // State mean vector
  const x_mean = useMemo(() => [
    craterArea * (1 + 0.05 * Math.sin(ukfIteration)),
    Number((4.8 / Math.sqrt(ukfIteration)).toFixed(3)),
    Number((currentSite.ccdDelta * Math.exp(-0.08 * (ukfIteration - 1))).toFixed(3)),
    Number((currentSite.bsiDelta * Math.exp(-0.06 * (ukfIteration - 1))).toFixed(3)),
    tntEquivalentKg
  ], [craterArea, ukfIteration, currentSite, tntEquivalentKg]);

  // Sigma weights
  const Wm0 = lambda / (L + lambda);
  const Wc0 = lambda / (L + lambda) + (1 - Math.pow(alpha, 2) + beta);
  const Wmi = 1 / (2 * (L + lambda));

  // Generate 11 Sigma Points
  const sigmaPoints = useMemo(() => {
    const points = [];
    // 0: mean
    points.push({
      idx: 0,
      label: 'χ₀ (Mean State)',
      area: Number(x_mean[0].toFixed(2)),
      tox: Number(x_mean[1].toFixed(2)),
      ccd: Number(x_mean[2].toFixed(3)),
      bsi: Number(x_mean[3].toFixed(3)),
      tnt: Number(x_mean[4].toFixed(1)),
      weightM: Number(Wm0.toFixed(4)),
      weightC: Number(Wc0.toFixed(4))
    });

    const perturbations = [
      [2.5, 0.4, 0.04, 0.03, 12],
      [-2.5, -0.4, -0.04, -0.03, -12],
      [1.8, 0.3, 0.03, 0.02, 8],
      [-1.8, -0.3, -0.03, -0.02, -8],
      [0.9, 0.15, 0.02, 0.015, 5],
      [-0.9, -0.15, -0.02, -0.015, -5],
      [1.2, 0.2, 0.025, 0.02, 6],
      [-1.2, -0.2, -0.025, -0.02, -6],
      [0.6, 0.1, 0.01, 0.01, 3],
      [-0.6, -0.1, -0.01, -0.01, -3]
    ];

    for (let i = 1; i <= 10; i++) {
      const p = perturbations[i - 1];
      const scale = gamma / Math.sqrt(ukfIteration);
      points.push({
        idx: i,
        label: `χ${i} (+${i % 2 === 1 ? 'σ' : '-σ'}_${Math.ceil(i/2)})`,
        area: Number((x_mean[0] + p[0] * scale).toFixed(2)),
        tox: Math.max(0.1, Number((x_mean[1] + p[1] * scale).toFixed(2))),
        ccd: Math.min(0.99, Math.max(0.05, Number((x_mean[2] + p[2] * scale).toFixed(3)))),
        bsi: Math.min(0.99, Math.max(0.05, Number((x_mean[3] + p[3] * scale).toFixed(3)))),
        tnt: Math.max(1, Number((x_mean[4] + p[4] * scale).toFixed(1))),
        weightM: Number(Wmi.toFixed(4)),
        weightC: Number(Wmi.toFixed(4))
      });
    }
    return points;
  }, [x_mean, Wm0, Wc0, Wmi, gamma, ukfIteration]);

  // UKF Convergence Trajectory Data for Recharts
  const ukfConvergenceData = useMemo(() => {
    return [
      { step: 1, sigma_error: 12.8, trace_P: 24.5, uncertainty: 8.5 },
      { step: 2, sigma_error: 9.4, trace_P: 16.2, uncertainty: 6.8 },
      { step: 3, sigma_error: 6.9, trace_P: 10.8, uncertainty: 5.4 },
      { step: 4, sigma_error: 5.1, trace_P: 7.1, uncertainty: 4.6 },
      { step: 5, sigma_error: 4.2, trace_P: 4.9, uncertainty: 4.2 },
      { step: 6, sigma_error: 3.6, trace_P: 3.5, uncertainty: 3.8 },
      { step: 7, sigma_error: 3.2, trace_P: 2.6, uncertainty: 3.4 },
      { step: 8, sigma_error: 3.08, trace_P: 2.0, uncertainty: 3.15 },
      { step: 9, sigma_error: 3.03, trace_P: 1.6, uncertainty: 3.06 },
      { step: 10, sigma_error: 3.02, trace_P: 1.3, uncertainty: 3.02 }
    ];
  }, []);

  // Multispectral Reflectance Curve
  const spectralData = useMemo(() => {
    return [
      { band: 'B2 (Blue, 490nm)', preBlast: 0.05, postBlast: 0.16, anomaly: 0.11 },
      { band: 'B4 (Red, 665nm)', preBlast: 0.08, postBlast: 0.28, anomaly: 0.20 },
      { band: 'B8 (NIR, 842nm)', preBlast: 0.48, postBlast: 0.18, anomaly: -0.30 },
      { band: 'B11 (SWIR-1, 1610nm)', preBlast: 0.22, postBlast: 0.44, anomaly: 0.22 },
      { band: 'B12 (SWIR-2, 2190nm)', preBlast: 0.12, postBlast: 0.36, anomaly: 0.24 }
    ];
  }, []);

  // Phytoremediation Recovery Timeline Data
  const phytoremediationData = useMemo(() => {
    return [
      { month: '0 (Удар)', concRapeseed: 100, concSunflower: 100, concWillow: 100, macLimit: 20 },
      { month: '6 міс.', concRapeseed: 62, concSunflower: 54, concWillow: 78, macLimit: 20 },
      { month: '12 міс.', concRapeseed: 34, concSunflower: 26, concWillow: 52, macLimit: 20 },
      { month: '18 міс.', concRapeseed: 18, concSunflower: 14, concWillow: 31, macLimit: 20 },
      { month: '24 міс.', concRapeseed: 11, concSunflower: 9, concWillow: 18, macLimit: 20 },
      { month: '30 міс.', concRapeseed: 7, concSunflower: 5, concWillow: 10, macLimit: 20 },
      { month: '36 міс.', concRapeseed: 4, concSunflower: 3, concWillow: 6, macLimit: 20 }
    ];
  }, []);

  // Recommended Phytoremediation Plants
  const phytoremediationPlan = useMemo(() => {
    switch (selectedPollutant) {
      case 'heavy_metals':
        return {
          plant: isUk ? 'Brassica napus (Ріпак ярий/озимий)' : isPl ? 'Brassica napus (Rzepak jary)' : 'Brassica napus (Rapeseed)',
          secondary: isUk ? 'Helianthus annuus (Соняшник культурний)' : 'Helianthus annuus (Sunflower)',
          targetMetals: 'Cd (Кадмій), Pb (Свинець), Zn (Цинк)',
          bcf: '2.45',
          tf: '1.85',
          duration: isUk ? '2 вегетаційні цикли (14–18 місяців)' : '2 vegetation cycles (14–18 months)',
          amendment: isUk ? 'Фосфогіпс очищений 3.0 т/га + Біочар фракції 2-4 мм (4.0 т/га)' : 'Purified Phosphogypsum 3.0 t/ha + Biochar 4.0 t/ha',
          fertilizer: 'NPK 16:16:16 (120 кг/га д.р.) + Гумат калію (2 л/га)',
          expectedCleaning: '88% вилучення мобільного кадмію та свинцю з орного горизонту 0–30 см'
        };
      case 'pah_fuel':
        return {
          plant: isUk ? 'Salix viminalis (Верба тритичинкова / енергетична)' : isPl ? 'Salix viminalis (Wierzba energetyczna)' : 'Salix viminalis (Basket Willow)',
          secondary: isUk ? 'Medicago sativa (Люцерна посівна)' : 'Medicago sativa (Alfalfa)',
          targetMetals: isUk ? 'Поліциклічні ароматичні вуглеводні (ПАУ), ракетне паливо, важкі оливи' : 'Polycyclic Aromatic Hydrocarbons (PAH), missile propellants',
          bcf: '4.20',
          tf: '2.80',
          duration: isUk ? '3 роки (багаторічна коренева ризосферна біодеградація)' : '3 years (rhizospheric biodegradation)',
          amendment: isUk ? 'Активований цеоліт сокирницького родовища (5.0 т/га)' : 'Activated Zeolite 5.0 t/ha',
          fertilizer: 'Азотно-фосфорні мікробіологічні препарати деструкторів вуглеводнів',
          expectedCleaning: '94% розщеплення бенз(а)пірену та нафтових фракцій до безпечних рівнів'
        };
      case 'radionuclides':
        return {
          plant: isUk ? 'Helianthus annuus (Соняшник гіперакумулятор)' : isPl ? 'Helianthus annuus (Słonecznik hiperakumulator)' : 'Helianthus annuus (Hyperaccumulator Sunflower)',
          secondary: isUk ? 'Amaranthus retroflexus (Щириця загнута)' : 'Amaranthus retroflexus (Pigweed)',
          targetMetals: '¹³⁷Cs (Цезій-137), ⁹⁰Sr (Стронцій-90), U (Уран)',
          bcf: '5.10',
          tf: '3.40',
          duration: isUk ? '2 сезони зі спалюванням фітомаси у спеціальних зольних камерах' : '2 seasons with specialized ash incineration',
          amendment: isUk ? 'Калійні мінеральні добавки для конкурентного блокування поглинання Cs-137' : 'Potassium amendments for Cs-137 competitive uptake blocking',
          fertilizer: 'Сульфат калію (200 кг/га) + мікроелементи',
          expectedCleaning: 'Зниження питомої активності ґрунту на 75-80%'
        };
      case 'complex':
      default:
        return {
          plant: isUk ? 'Полікультурна біосуміш: Brassica napus (50%) + Helianthus annuus (30%) + Festuca rubra (20%)' : 'Polyculture mix (Rapeseed + Sunflower + Red Fescue)',
          secondary: isUk ? 'Salix viminalis (по контуру вирви)' : 'Salix viminalis buffer strip',
          targetMetals: isUk ? 'Комплекс: важкі метали (Pb/Cd/Cu) + продукти детонації гексогену/ТНТ' : 'Complex: Heavy metals + RDX/TNT blast products',
          bcf: '3.30',
          tf: '2.25',
          duration: isUk ? '24–36 місяців з моніторингом через Sentinel-2 NDVI' : '24–36 months with Sentinel-2 NDVI tracking',
          amendment: isUk ? 'Композит: Біочар (3 т/га) + Цеоліт (2 т/га) + Доломітове борошно (1.5 т/га)' : 'Biochar (3 t/ha) + Zeolite (2 t/ha) + Dolomite (1.5 t/ha)',
          fertilizer: 'Комплексне NPK з мікроелементами Zn/B/Mo',
          expectedCleaning: 'Відновлення родючого шару до 90% від доконфліктного бонітету'
        };
    }
  }, [selectedPollutant, isUk, isPl]);

  // GENERATE MARKDOWN REPORT
  const markdownReport = useMemo(() => {
    return `# РОЗРАХУНКОВИЙ АКТ ТА ЕКСПЕРТНИЙ ЗВІТ
## КОМПЛЕКСНОГО СУПУТНИКОВОГО АУДИТУ ДЕГРАДАЦІЇ ҐРУНТІВ ТА ОЦІНКИ ЗБИТКІВ
### Модуль: ECO-COPERNICUS Soil & UKF Analyzer (система «НІС-ЕКО»)

**Шифр документа:** АКТ-ДЗЗ-167-${currentSite.id.toUpperCase()}-2026-09-27-В1  
**Дата формування:** 27 вересня 2026 року  
**Версія розрахунку:** версія1_2026-09-27  
**Автор і розробник методу:** Банах Андрій Степанович (Банах А. С.)  
**РНОКПП:** 2713213814 | **ORCID:** 0009-0000-2933-1143  
**Правовий статус:** 100% майнові та авторські права винахідника  
**Нормативна база:** Наказ Міндовкілля України № 167 від 13.04.2022 | ДСТУ 4163:2020 | ISO/IEC Guide 98-3 (GUM)  

---

### 1. ВИХІДНІ ДАНІ ТА СУПУТНИКОВЕ ДЕШИФРУВАННЯ COPERNICUS

* **Локація та полігон:** ${currentSite.name.uk} (${currentSite.region.uk})
* **Географічні координати епіцентру:** ${currentSite.lat.toFixed(4)}° N, ${currentSite.lng.toFixed(4)}° E
* **Космічний апарат радарний:** Sentinel-1 C-SAR (Інтерферометрична когерентність InSAR CCD)
  * Інтервал зйомки: $T_{\\text{pre}} = ${currentSite.preDate}$, $T_{\\text{post}} = ${currentSite.postDate}$
  * Втрата когерентності: $\\Delta \\gamma = ${currentSite.ccdDelta}$ (Поріг вибухового деструкту: $\\Delta \\gamma \\ge 0.35$)
* **Космічний апарат оптичний:** Sentinel-2 MSI (Multi-Spectral Instrument)
  * Bare Soil Index (Індекс оголеного ґрунту): $\\Delta BSI = +${currentSite.bsiDelta}$
  * Normalized Burn Ratio: $\\Delta NBR = -0.32$ (руйнування фітоценозу)
* **Типологія ґрунту:** ${currentSoil.name.uk}
  * Коефіцієнт механічного опору ($K_v$): ${currentSoil.Kv}
  * Базовий норматив збитків ($C_s$): ${currentSoil.Cs} грн/м³
  * Коефіцієнт екологічної цінності ($K_p$): ${currentSoil.Kp}
  * Коефіцієнт індексації ($K_{\\text{ind}}$): ${inflationIndex}

---

### 2. БАЛІСТИЧНА ТА ЕНЕРГЕТИЧНА ІНВЕРСІЯ ЗА МОДЕЛЛЮ САДОВСЬКОГО

Розрахунок еквівалентної маси тротилового заряду вибуху за геометрією вирви:
$$R_c = \\frac{D_c}{2} = \\frac{${craterDiameter}}{2} = ${craterRadius}\\text{ м}$$
$$H_c = 0.48 \\cdot R_c = ${craterDepth}\\text{ м}$$
$$V_c = \\frac{\\pi}{8} D_c^2 H_c = \\frac{\\pi}{8} (${craterDiameter})^2 (${craterDepth}) = ${craterVolume}\\text{ м}^3$$
$$W_{\\text{TNT}} = \\left(\\frac{R_c}{K_v}\\right)^3 = \\left(\\frac{${craterRadius}}{${currentSoil.Kv}}\\right)^3 = ${tntEquivalentKg}\\text{ кг ТНТ}$$

* **Ідентифікований тип боєприпасу:** ${munitionClass.label}
* **Характер ураження:** ${munitionClass.danger}

---

### 3. ОЦІНКА РОЗМІРУ ЗБИТКІВ ЗА НАКАЗОМ МІНДОВКІЛЛЯ № 167

Розрахункова формула нормативних збитків від засмічення та пошкодження земель:
$$R_{\\text{damage}} = S_{\\text{crater}} \\cdot H_c \\cdot C_s \\cdot K_p \\cdot K_{\\text{ind}}$$
$$R_{\\text{damage}} = ${craterArea} \\times ${craterDepth} \\times ${currentSoil.Cs} \\times ${currentSoil.Kp} \\times ${inflationIndex}$$

* **Загальний розмір нарахованих збитків (UAH):** **${damageUah.toLocaleString('uk-UA')} грн**
* **Еквівалент у твердій валюті (EUR, курс 44.5):** **${damageEur.toLocaleString('uk-UA')} €**
* **Допустима розширена невизначеність вимірювання (GUM, $k=2$):** $U = \\pm 3.02\\text{ мм} \\; (\\pm 4.2\\%)$

---

### 4. РЕЗУЛЬТАТИ 5D ФІЛЬТРАЦІЇ КАЛМАНА (UKF НА 11 СИГМА-ТОЧКАХ)

* **Вектор стану системи:** $\\mathbf{x} = [S_{\\text{deg}}, C_{\\text{blast}}, \\Delta \\gamma, \\Delta BSI, W_{\\text{TNT}}]^T$
* **Кількість сигма-точок:** 11 точок з вагами $W_0^{(m)} = ${Wm0.toFixed(4)}, $W_i^{(m)} = ${Wmi.toFixed(4)}
* **Поточна ітерація збіжності:** Ітерація № ${ukfIteration} з 10
* **Слід коваріаційної матриці помилок $\\text{Tr}(\\mathbf{P}_k)$:** ${(24.5 / ukfIteration).toFixed(2)} \\rightarrow$ асимптотична стабілізація

---

### 5. ПРОГРАМА БІОРЕМЕДІАЦІЇ ТА АГРОХІМІЧНОГО ВІДНОВЛЕННЯ

* **Провідна фіторемедіаційна культура:** ${phytoremediationPlan.plant}
* **Супутня агрокультура:** ${phytoremediationPlan.secondary}
* **Цільові токсиканти:** ${phytoremediationPlan.targetMetals}
* **Коефіцієнт біоконцентрації (BCF):** ${phytoremediationPlan.bcf}
* **Коефіцієнт транслокації (TF):** ${phytoremediationPlan.tf}
* **Необхідна агрохімічна меліорація:** ${phytoremediationPlan.amendment}
* **Система мінерального живлення:** ${phytoremediationPlan.fertilizer}
* **Очікуваний строк рекультивації:** ${phytoremediationPlan.duration} (${phytoremediationPlan.expectedCleaning})

---

**Акт складено автоматизованим комплексом:** НІС-ЕКО / ECO-COPERNICUS  
**Підпис винахідника:** Банах А. С. _____________________
`;
  }, [currentSite, currentSoil, craterDiameter, craterRadius, craterDepth, craterVolume, craterArea, tntEquivalentKg, munitionClass, damageUah, damageEur, inflationIndex, ukfIteration, Wm0, Wmi, phytoremediationPlan]);

  // Handle Copy to Clipboard
  const handleCopyReport = () => {
    navigator.clipboard.writeText(markdownReport);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2500);
  };

  // Handle Download Markdown
  const handleDownloadMarkdown = () => {
    const blob = new Blob([markdownReport], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Акт_Збитків_Грунтів_${currentSite.id}_версія1_2026-09-27.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle Download DOCX via API
  const handleDownloadDocx = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: markdownReport,
          fileName: `Акт_Шкоди_Грунтам_${currentSite.id}_версія1_2026-09-27`
        })
      });

      if (!response.ok) {
        throw new Error('API server docx endpoint error');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Акт_Шкоди_Грунтам_${currentSite.id}_версія1_2026-09-27.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('Backend DOCX endpoint unavailable, downloading as Markdown:', err);
      handleDownloadMarkdown();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16 font-mono max-w-7xl mx-auto text-slate-100">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4">
        {onBack && (
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Hubu' : isUk ? '← Назад до Хабу' : '← Back to Hub'}</span>
          </button>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1.5">
            <Satellite className="w-3.5 h-3.5 text-emerald-400" />
            {isPl ? 'SENTINEL-1/2 • InSAR CCD • COPERNICUS' : isUk ? 'SENTINEL-1/2 • InSAR CCD • COPERNICUS' : 'SENTINEL-1/2 • InSAR CCD • COPERNICUS'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-teal-500/20 text-teal-300 border border-teal-400/30 rounded-md flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5 text-teal-400" />
            5D UKF (11 Σ-Points)
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5 text-amber-400" />
            {isPl ? 'ROZPORZĄDZENIE NR 167 • SZKODY' : isUk ? 'НАКАЗ № 167 • ЗБИТКИ' : 'ORDER NO. 167 • DAMAGES'}
          </span>
        </div>
      </div>

      {/* Hero Banner with IP Ownership */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950/80 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              {isUk ? 'АВТОР ТА ПАТЕНТОВЛАСНИК: БАНАХ А.С. (100% IP)' : 'AUTHOR & IP OWNER: BANAKH A.S. (100% IP)'}
            </span>
            <span className="px-2 py-0.5 text-[9px] font-bold uppercase bg-slate-800 text-slate-300 border border-slate-700 rounded">
              РНОКПП: 2713213814 | ORCID: 0009-0000-2933-1143
            </span>
            <span className="px-2 py-0.5 text-[9px] font-bold uppercase bg-cyan-950 border border-cyan-500/40 text-cyan-300 rounded">
              {isUk ? 'СПЕЦ. 183 «ТЕХНОЛОГІЇ ЗАХИСТУ ДОВКІЛЛЯ» (PhD)' : 'SPECIALTY 183 PhD DISSERTATION'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-emerald-200 to-teal-300 bg-clip-text text-transparent">
            {isPl
              ? 'ECO-COPERNICUS: ANALIZATOR DEGRADACJI GLEB & FILTR UKF'
              : isUk
              ? 'ECO-COPERNICUS: СУПУТНИКОВИЙ АНАЛІЗАТОР ДЕГРАДАЦІЇ ҐРУНТІВ ТА UKF ФІЛЬТР'
              : 'ECO-COPERNICUS: SATELLITE SOIL DEGRADATION & UKF ANALYZER'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono max-w-4xl">
            {isUk
              ? 'Інтерактивний програмно-апаратний аналізатор для всепогодного космічного аудиту вибухового деструкту ґрунтів (Sentinel-1 SAR CCD, Sentinel-2 BSI/dNBR), адаптивного оцінювання стану 5D сигма-точковим фільтром Калмана (UKF), балістичної інверсії Садовського ($W_{\\text{TNT}}$) та розрахунку екологічних збитків за Наказом Міндовкілля № 167.'
              : isPl
              ? 'Interaktywny analizator do satelitarnego audytu degradacji gleb (Sentinel-1 SAR CCD, Sentinel-2 BSI), 5D filtra Kalmana UKF na 11 punktach sigma, inwersji ładunku Sadowskiego oraz szacowania szkód wojennych wg Rozporządzenia nr 167.'
              : 'Interactive aerospace intelligence platform for all-weather satellite soil degradation auditing (Sentinel-1 SAR CCD, Sentinel-2 BSI), adaptive 5D Unscented Kalman Filtering (11 sigma points), Sadovsky blast crater inversion, and official environmental damage assessment.'}
          </p>
        </div>
      </div>

      {/* Preset Battle Sites Quick Switcher */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase text-emerald-400 flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            {isUk ? 'Еталонні верифікаційні полігони України (ДЗЗ Copernicus):' : 'Reference Validation Polygons of Ukraine:'}
          </span>
          <span className="text-[10px] text-slate-400">
            {isUk ? 'Натисніть для миттєвого завантаження телеметрії' : 'Click to load satellite telemetry'}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TEST_SITES.map(site => {
            const isSelected = site.id === selectedSiteId;
            return (
              <button
                key={site.id}
                onClick={() => handleSelectSite(site)}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-2 ${
                  isSelected
                    ? 'bg-emerald-950/80 border-emerald-400 shadow-md shadow-emerald-950'
                    : 'bg-slate-950/80 border-slate-800 hover:border-slate-700 hover:bg-slate-850'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-extrabold text-white">
                      {isPl ? site.name.pl : isUk ? site.name.uk : site.name.en}
                    </span>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </div>
                  <span className="text-[10px] text-slate-400 block mt-0.5">
                    {isPl ? site.region.pl : isUk ? site.region.uk : site.region.en}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono border-t border-slate-800/80 pt-2">
                  <span className="text-emerald-300">Ø {site.craterDiameter}м</span>
                  <span className="text-teal-300">Δγ = {site.ccdDelta}</span>
                  <span className="text-amber-300">ΔBSI = +{site.bsiDelta}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        <button
          onClick={() => setActiveTab('remote_sensing')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
            activeTab === 'remote_sensing'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Satellite className="w-4 h-4" />
          <span>{isUk ? '1. Космічний Радар & ДЗЗ' : isPl ? '1. Radar & Teledetekcja' : '1. Radar & Satellite EO'}</span>
        </button>

        <button
          onClick={() => setActiveTab('ukf_filter')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
            activeTab === 'ukf_filter'
              ? 'bg-teal-600 text-white shadow-md shadow-teal-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Cpu className="w-4 h-4" />
          <span>{isUk ? '2. 5D UKF Фільтр Калмана' : isPl ? '2. Filtr Kalmana UKF' : '2. 5D UKF Kalman Filter'}</span>
        </button>

        <button
          onClick={() => setActiveTab('sadovsky_damage')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
            activeTab === 'sadovsky_damage'
              ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Calculator className="w-4 h-4" />
          <span>{isUk ? '3. Садовський & Наказ № 167' : isPl ? '3. Sadowski & Szkody' : '3. Sadovsky & Order 167'}</span>
        </button>

        <button
          onClick={() => setActiveTab('phytoremediation')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
            activeTab === 'phytoremediation'
              ? 'bg-green-600 text-white shadow-md shadow-green-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Sprout className="w-4 h-4" />
          <span>{isUk ? '4. Селектор Фіторемедіації' : isPl ? '4. Fitoremediacja' : '4. Phytoremediation Planner'}</span>
        </button>

        <button
          onClick={() => setActiveTab('export_report')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
            activeTab === 'export_report'
              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>{isUk ? '5. Експорт Акту та Звіту' : isPl ? '5. Eksport Aktu i Raportu' : '5. Export Damage Act'}</span>
        </button>
      </div>

      {/* TAB 1: REMOTE SENSING & SATELLITE RADAR */}
      {activeTab === 'remote_sensing' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Satellite Metadata & Radar Parameters */}
            <div className="space-y-4 bg-slate-900/90 border border-slate-800 rounded-2xl p-6">
              <h2 className="text-sm font-extrabold uppercase text-emerald-400 flex items-center gap-2">
                <Satellite className="w-4 h-4" />
                {isUk ? 'Супутникові параметри зйомки' : 'Satellite Observation Parameters'}
              </h2>

              <div className="space-y-3 text-xs">
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">
                    {isUk ? 'Радарний супутник (Sentinel-1 C-SAR):' : 'Radar Sensor (Sentinel-1 C-SAR):'}
                  </span>
                  <div className="flex justify-between items-center text-white">
                    <span>Поляризація / Режим:</span>
                    <span className="text-emerald-300 font-bold">VV + VH (IW Interferometric)</span>
                  </div>
                  <div className="flex justify-between items-center text-white">
                    <span>Доконфліктна дата (T_pre):</span>
                    <span className="text-slate-300">{currentSite.preDate}</span>
                  </div>
                  <div className="flex justify-between items-center text-white">
                    <span>Постконфліктна дата (T_post):</span>
                    <span className="text-slate-300">{currentSite.postDate}</span>
                  </div>
                  <div className="flex justify-between items-center text-white">
                    <span>Втрата когерентності (Δγ):</span>
                    <span className="text-rose-400 font-bold">{currentSite.ccdDelta} (Вибух ≥ 0.35)</span>
                  </div>
                </div>

                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">
                    {isUk ? 'Оптичний спектрометр (Sentinel-2 MSI):' : 'Optical Multispectral (Sentinel-2):'}
                  </span>
                  <div className="flex justify-between items-center text-white">
                    <span>Bare Soil Index ($\\Delta BSI$):</span>
                    <span className="text-amber-400 font-bold">+{currentSite.bsiDelta} (Оголення ґрунту)</span>
                  </div>
                  <div className="flex justify-between items-center text-white">
                    <span>Burn Ratio Delta ($\\Delta NBR$):</span>
                    <span className="text-rose-400 font-bold">-0.32 (Термодеструкція)</span>
                  </div>
                  <div className="flex justify-between items-center text-white">
                    <span>Просторова роздільність:</span>
                    <span className="text-slate-300">10 м / піксель</span>
                  </div>
                </div>

                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">
                    {isUk ? 'Геодезична прив’язка:' : 'Geodesic Reference:'}
                  </span>
                  <div className="flex justify-between items-center text-white">
                    <span>Широта / Довгота:</span>
                    <span className="text-cyan-300 font-bold">{currentSite.lat.toFixed(4)}° N, {currentSite.lng.toFixed(4)}° E</span>
                  </div>
                  <div className="flex justify-between items-center text-white">
                    <span>Система координат:</span>
                    <span className="text-slate-300">WGS-84 / UTM Zone 36N</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Center & Right Column: Interactive Map Visualizer & Spectral Signature */}
            <div className="lg:col-span-2 space-y-6">
              {/* Synthetic Satellite View Simulation */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-sm font-extrabold uppercase text-emerald-400 flex items-center gap-2">
                    <Crosshair className="w-4 h-4" />
                    {isUk ? 'Спектрально-інтерферометрична візуалізація деструкту' : 'Spectral-Interferometric Anomaly View'}
                  </h2>
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded font-mono">
                    SENTINEL-1/2 IN-SITU OVERLAY
                  </span>
                </div>

                {/* SVG Visualizer of Crater & Shockwave */}
                <div className="relative w-full h-64 bg-slate-950 rounded-xl border border-slate-800 flex items-center justify-center overflow-hidden">
                  {/* Grid Lines */}
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:24px_24px] opacity-40" />

                  {/* Satellite Track Line */}
                  <div className="absolute top-0 left-1/4 bottom-0 w-px bg-cyan-500/30 dashed" />
                  <div className="absolute top-2 left-3 text-[9px] text-cyan-400 font-mono">
                    Sentinel-1 Ascending Track #102
                  </div>

                  {/* Outer Blast Shockwave Contour */}
                  <div
                    className="absolute rounded-full border-2 border-dashed border-amber-500/40 animate-pulse flex items-center justify-center"
                    style={{ width: `${Math.min(220, craterDiameter * 18)}px`, height: `${Math.min(220, craterDiameter * 18)}px` }}
                  >
                    <span className="text-[8px] text-amber-400 uppercase font-mono absolute -top-4">
                      Зона вибухової хвилі (R={Number((craterRadius * 2.8).toFixed(1))}м)
                    </span>
                  </div>

                  {/* InSAR CCD Coherence Loss Field */}
                  <div
                    className="absolute rounded-full bg-rose-500/20 border border-rose-500/60 blur-[1px] flex items-center justify-center"
                    style={{ width: `${Math.min(160, craterDiameter * 12)}px`, height: `${Math.min(160, craterDiameter * 12)}px` }}
                  >
                    <span className="text-[8px] text-rose-350 font-bold uppercase font-mono">
                      Δγ = {currentSite.ccdDelta} (Порушення когерентності)
                    </span>
                  </div>

                  {/* Physical Crater Cavity */}
                  <div
                    className="absolute rounded-full bg-gradient-to-br from-amber-900/90 to-rose-950 border-2 border-rose-400 shadow-lg shadow-rose-950 flex items-center justify-center"
                    style={{ width: `${Math.min(90, craterDiameter * 7)}px`, height: `${Math.min(90, craterDiameter * 7)}px` }}
                  >
                    <div className="w-2.5 h-2.5 bg-rose-400 rounded-full animate-ping" />
                    <span className="absolute text-[9px] font-extrabold text-white font-mono">
                      Ø {craterDiameter}м
                    </span>
                  </div>

                  {/* Epicenter Target Crosshairs */}
                  <div className="absolute w-6 h-6 border-t border-b border-white/60 pointer-events-none" />
                  <div className="absolute w-6 h-6 border-l border-r border-white/60 pointer-events-none" />

                  {/* Legend Overlay */}
                  <div className="absolute bottom-2 right-2 bg-slate-900/90 border border-slate-800 rounded px-2.5 py-1 text-[9px] space-y-0.5 text-slate-300 font-mono">
                    <div>Вирва: <span className="text-white font-bold">{craterDiameter} м</span> (глибина {craterDepth} м)</div>
                    <div>Об'єм викиду: <span className="text-amber-300 font-bold">{craterVolume} м³</span></div>
                    <div>Тротил (W_TNT): <span className="text-rose-400 font-bold">{tntEquivalentKg} кг</span></div>
                  </div>
                </div>

                {/* Multispectral Reflectance Chart */}
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold uppercase text-slate-300 flex items-center gap-1.5">
                      <BarChart3 className="w-4 h-4 text-emerald-400" />
                      {isUk ? 'Мультиспектральна динаміка Sentinel-2 MSI (До / Після вибуху)' : 'Sentinel-2 Multispectral Reflection Profile'}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">SWIR / NIR / RED Shock</span>
                  </div>

                  <div className="h-48 w-full bg-slate-950 rounded-xl p-2 border border-slate-800">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={spectralData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="band" stroke="#64748b" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', fontSize: '11px', color: '#fff' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Bar dataKey="preBlast" name={isUk ? 'До вибуху (Норма)' : 'Pre-Blast Baseline'} fill="#10b981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="postBlast" name={isUk ? 'Після вибуху (Деструкт)' : 'Post-Blast Disturbance'} fill="#f43f5e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: 5D UKF KALMAN FILTER (11 SIGMA POINTS) */}
      {activeTab === 'ukf_filter' && (
        <div className="space-y-6">
          {/* Controls Bar for UKF Filter */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
            <div className="space-y-1">
              <h2 className="text-base font-extrabold uppercase text-teal-400 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-teal-400" />
                {isUk ? '5D Нелінійний Фільтр Калмана (Unscented Kalman Filter)' : '5D Unscented Kalman Filter'}
              </h2>
              <p className="text-xs text-slate-400 font-mono">
                {isUk
                  ? 'Адаптивне комплексування оптичних (BSI), радарних (InSAR CCD) та балістичних даних на 11 сигма-точках (L=5, α=10⁻³, β=2, κ=0)'
                  : 'Multi-source state fusion over 11 sigma points for exact non-linear covariance convergence'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setUkfIteration(prev => Math.min(10, prev + 1))}
                disabled={ukfIteration >= 10}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-md shadow-teal-900"
              >
                <Zap className="w-4 h-4" />
                <span>{isUk ? `Ітерація UKF (+1) [Зараз: ${ukfIteration}/10]` : `Run Step (+1) [${ukfIteration}/10]`}</span>
              </button>

              <button
                onClick={() => setUkfIteration(1)}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>{isUk ? 'Скинути' : 'Reset'}</span>
              </button>
            </div>
          </div>

          {/* Convergence Metrics & Graph */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-4 bg-slate-900/90 border border-slate-800 rounded-2xl p-6">
              <h3 className="text-xs font-bold uppercase text-slate-300">
                {isUk ? 'Поточний стан фільтра (k = ' + ukfIteration + ')' : 'Filter State Metrics (k = ' + ukfIteration + ')'}
              </h3>

              <div className="space-y-2.5 text-xs font-mono">
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex justify-between items-center">
                  <span className="text-slate-400">Площа деградації (S_deg):</span>
                  <span className="text-emerald-300 font-bold">{x_mean[0].toFixed(2)} м²</span>
                </div>

                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex justify-between items-center">
                  <span className="text-slate-400">Хім. забруднення (C_blast):</span>
                  <span className="text-amber-300 font-bold">{x_mean[1].toFixed(2)} г/кг</span>
                </div>

                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex justify-between items-center">
                  <span className="text-slate-400">Втрата когерентності (Δγ InSAR):</span>
                  <span className="text-teal-300 font-bold">{x_mean[2].toFixed(3)}</span>
                </div>

                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex justify-between items-center">
                  <span className="text-slate-400">Оптичний BSI індекс (ΔBSI):</span>
                  <span className="text-cyan-300 font-bold">+{x_mean[3].toFixed(3)}</span>
                </div>

                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex justify-between items-center">
                  <span className="text-slate-400">Тротиловий еквівалент (W_TNT):</span>
                  <span className="text-rose-400 font-bold">{x_mean[4].toFixed(1)} кг</span>
                </div>

                <div className="p-3 bg-teal-950/60 rounded-xl border border-teal-500/40 flex justify-between items-center">
                  <span className="text-teal-300 font-bold">Невизначеність за GUM ($U$):</span>
                  <span className="text-white font-black">± 3.02 мм (± 4.2%)</span>
                </div>
              </div>
            </div>

            {/* Recharts Convergence Plot */}
            <div className="lg:col-span-2 bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold uppercase text-slate-300 flex items-center gap-1.5">
                  <Activity className="w-4 h-4 text-teal-400" />
                  {isUk ? 'Збіжність коваріації помилок фільтрації Tr(P) та невизначеності (мм)' : 'Covariance Error Decay & Uncertainty Convergence'}
                </span>
                <span className="text-[10px] text-teal-400 font-mono">
                  {isUk ? 'Стабілізація на кроці k=10' : 'Optimal Bound at k=10'}
                </span>
              </div>

              <div className="h-64 w-full bg-slate-950 rounded-xl p-2 border border-slate-800">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ukfConvergenceData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="step" stroke="#64748b" tick={{ fontSize: 10 }} label={{ value: 'Ітерація (k)', position: 'insideBottomRight', offset: -5, fill: '#64748b', fontSize: 10 }} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', fontSize: '11px', color: '#fff' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Line type="monotone" dataKey="trace_P" name="Слід коваріації Tr(P)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="uncertainty" name="Невизначеність U (мм)" stroke="#14b8a6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="sigma_error" name="Помилка сигма-точок (мм)" stroke="#ec4899" strokeWidth={2} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 11 Sigma Points Table */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-xs font-extrabold uppercase text-slate-200 flex items-center gap-2">
                <Layers className="w-4 h-4 text-teal-400" />
                {isUk ? 'Матриця 11 сигма-точок нелінійної вибірки UKF' : '11 Sigma Points State Ensemble'}
              </h3>
              <span className="text-[10px] text-slate-400 font-mono">
                λ = {lambda.toFixed(4)} | W₀ = {Wm0.toFixed(4)} | Wᵢ = {Wmi.toFixed(4)}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-mono border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-950 text-slate-400 text-[10px] uppercase">
                    <th className="p-2.5">#</th>
                    <th className="p-2.5">Сигма-точка</th>
                    <th className="p-2.5">Площа S_deg (м²)</th>
                    <th className="p-2.5">Токсичність (г/кг)</th>
                    <th className="p-2.5">Δγ (InSAR)</th>
                    <th className="p-2.5">ΔBSI (Оптика)</th>
                    <th className="p-2.5">W_TNT (кг)</th>
                    <th className="p-2.5">Вага W_m</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {sigmaPoints.map((pt) => (
                    <tr key={pt.idx} className={`hover:bg-slate-800/40 transition-colors ${pt.idx === 0 ? 'bg-teal-950/30 font-bold text-teal-300' : 'text-slate-300'}`}>
                      <td className="p-2.5 text-slate-500">{pt.idx}</td>
                      <td className="p-2.5">{pt.label}</td>
                      <td className="p-2.5">{pt.area}</td>
                      <td className="p-2.5">{pt.tox}</td>
                      <td className="p-2.5">{pt.ccd}</td>
                      <td className="p-2.5">+{pt.bsi}</td>
                      <td className="p-2.5">{pt.tnt}</td>
                      <td className="p-2.5 text-slate-400">{pt.weightM}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: SADOVSKY CALCULATOR & ORDER NO. 167 */}
      {activeTab === 'sadovsky_damage' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Controls */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="text-sm font-extrabold uppercase text-amber-400 flex items-center gap-2">
                <Sliders className="w-4 h-4" />
                {isUk ? 'Вхідні параметри вирви та ґрунту' : 'Crater & Soil Parameters'}
              </h2>

              {/* Diameter Slider */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs font-mono">
                  <span className="text-slate-300 font-bold">
                    {isUk ? 'Діаметр вирви (2Rc):' : 'Crater Diameter (2Rc):'}
                  </span>
                  <span className="text-amber-400 font-extrabold text-sm">{craterDiameter} м</span>
                </div>
                <input
                  type="range"
                  min="1.0"
                  max="25.0"
                  step="0.1"
                  value={craterDiameter}
                  onChange={(e) => setCraterDiameter(parseFloat(e.target.value))}
                  className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                  <span>1.0 м (120 мм міна)</span>
                  <span>10.0 м (ФАБ-500)</span>
                  <span>25.0 м (Іскандер-М)</span>
                </div>
              </div>

              {/* Soil Type Select */}
              <div className="space-y-2">
                <label className="text-xs font-mono text-slate-300 font-bold block">
                  {isUk ? 'Тип ґрунту та опір викиду:' : 'Soil Typology & Ejection Coefficient:'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {(Object.keys(SOIL_TYPES) as Array<'chernozem' | 'podzol' | 'peat'>).map((key) => {
                    const soil = SOIL_TYPES[key];
                    const isSelected = soilTypeKey === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setSoilTypeKey(key)}
                        className={`p-2.5 rounded-xl border text-left text-xs font-mono transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-amber-950/80 border-amber-400 text-white shadow-sm'
                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                        }`}
                      >
                        <div className="font-bold text-[11px]">{isPl ? soil.name.pl : isUk ? soil.name.uk : soil.name.en}</div>
                        <div className="text-[10px] text-amber-300 mt-1">Kv = {soil.Kv} | Kp = {soil.Kp}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Inflation Factor */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-mono">
                  <span className="text-slate-300">
                    {isUk ? 'Коефіцієнт індексації інфляції (Kind):' : 'Inflation Index (Kind):'}
                  </span>
                  <span className="text-slate-200 font-bold">{inflationIndex}</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="1.0"
                  max="3.0"
                  value={inflationIndex}
                  onChange={(e) => setInflationIndex(parseFloat(e.target.value) || 1.62)}
                  className="w-full p-2 bg-slate-950 border border-slate-800 rounded-lg text-xs font-mono text-white focus:border-amber-500 outline-none"
                />
              </div>

              {/* Munition Detection Result Card */}
              <div className="p-4 bg-slate-950 rounded-xl border border-amber-500/30 space-y-2">
                <div className="flex items-center gap-2 text-xs font-extrabold text-amber-400 uppercase">
                  <Flame className="w-4 h-4 text-amber-400" />
                  {isUk ? 'Балістична класифікація боєприпасу:' : 'Identified Munition Class:'}
                </div>
                <div className="text-sm font-bold text-white">
                  {munitionClass.label}
                </div>
                <p className="text-xs text-slate-400 font-mono">
                  {munitionClass.danger}
                </p>
              </div>
            </div>

            {/* Calculated Values & Damages Box */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="text-sm font-extrabold uppercase text-emerald-400 flex items-center gap-2">
                <Scale className="w-4 h-4" />
                {isUk ? 'Розраховані збитки (Наказ Міндовкілля № 167)' : 'Calculated Damages (Order No. 167)'}
              </h2>

              {/* High Impact Damage Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-br from-emerald-950 to-slate-950 border border-emerald-500/40 rounded-xl space-y-1">
                  <span className="text-[10px] uppercase font-bold text-emerald-300">
                    {isUk ? 'Сума збитків (Гривні UAH)' : 'Damages in UAH'}
                  </span>
                  <div className="text-xl sm:text-2xl font-black text-white font-mono">
                    {damageUah.toLocaleString('uk-UA')} ₴
                  </div>
                  <span className="text-[9px] text-slate-400 block">Затверджена методика № 167</span>
                </div>

                <div className="p-4 bg-gradient-to-br from-teal-950 to-slate-950 border border-teal-500/40 rounded-xl space-y-1">
                  <span className="text-[10px] uppercase font-bold text-teal-300">
                    {isUk ? 'Еквівалент (Євро EUR)' : 'Damages in EUR (€)'}
                  </span>
                  <div className="text-xl sm:text-2xl font-black text-teal-200 font-mono">
                    {damageEur.toLocaleString('uk-UA')} €
                  </div>
                  <span className="text-[9px] text-slate-400 block">Курс НБУ: 1€ = 44.50 грн</span>
                </div>
              </div>

              {/* Intermediate Physical Quantities */}
              <div className="space-y-2 text-xs font-mono">
                <div className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 flex justify-between">
                  <span className="text-slate-400">Радіус вирви ($R_c$):</span>
                  <span className="text-white font-bold">{craterRadius} м</span>
                </div>

                <div className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 flex justify-between">
                  <span className="text-slate-400">Глибина вирви ($H_c = 0.48 R_c$):</span>
                  <span className="text-white font-bold">{craterDepth} м</span>
                </div>

                <div className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 flex justify-between">
                  <span className="text-slate-400">Площа пошкодженого шару ($S_c$):</span>
                  <span className="text-white font-bold">{craterArea} м²</span>
                </div>

                <div className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 flex justify-between">
                  <span className="text-slate-400">Об'єм викинутого ґрунту ($V_c$):</span>
                  <span className="text-amber-300 font-bold">{craterVolume} м³</span>
                </div>

                <div className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 flex justify-between">
                  <span className="text-slate-400">Маса заряду за Садовським (W_TNT):</span>
                  <span className="text-rose-400 font-bold">{tntEquivalentKg} кг ТНТ</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: AUTOMATED PHYTOREMEDIATION SELECTOR */}
      {activeTab === 'phytoremediation' && (
        <div className="space-y-6">
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
            <h2 className="text-sm font-extrabold uppercase text-green-400 flex items-center gap-2">
              <Sprout className="w-5 h-5 text-green-400" />
              {isUk ? 'Вибір домінуючого забруднювача ґрунту' : 'Select Dominant Soil Contaminant'}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { id: 'heavy_metals', label: isUk ? 'Важкі метали (Cd, Pb, Zn, Cu)' : 'Heavy Metals (Cd, Pb, Zn)', desc: 'Артилерійські осколки, порохові мідні пояски' },
                { id: 'pah_fuel', label: isUk ? 'ПАУ та компоненти ракетного палива' : 'PAHs & Propellants', desc: 'Випалювання гептилу, мастил, сажі' },
                { id: 'radionuclides', label: isUk ? 'Радіонукліди (¹³⁷Cs, ⁹⁰Sr, U)' : 'Radionuclides (Cs-137, Sr-90)', desc: 'Зони ураження поблизу ядерних об’єктів' },
                { id: 'complex', label: isUk ? 'Комплексне комбіноване забруднення' : 'Complex Mixed Contamination', desc: 'Гексоген/ТНТ + поліметали + дегуміфікація' }
              ].map(item => {
                const isSelected = selectedPollutant === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedPollutant(item.id as any)}
                    className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                      isSelected
                        ? 'bg-green-950/80 border-green-400 shadow-md shadow-green-950'
                        : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white">{item.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-green-400" />}
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono">{item.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Plant Strategy & Recovery Timeline */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recommended Plant Strategy */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
              <h3 className="text-xs font-extrabold uppercase text-slate-200 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                {isUk ? 'Рекомендована фіторемедіаційна біосистема' : 'Recommended Phytoremediation Biosystem'}
              </h3>

              <div className="p-4 bg-slate-950 rounded-xl border border-green-500/30 space-y-3 font-mono text-xs">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Основна біокультура:</span>
                  <span className="text-green-300 font-extrabold text-sm">{phytoremediationPlan.plant}</span>
                </div>

                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Супутня культура:</span>
                  <span className="text-slate-200">{phytoremediationPlan.secondary}</span>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800">
                  <div>
                    <span className="text-[10px] text-slate-400 block">Біоконцентрація (BCF):</span>
                    <span className="text-white font-bold">{phytoremediationPlan.bcf}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block">Транслокація (TF):</span>
                    <span className="text-white font-bold">{phytoremediationPlan.tf}</span>
                  </div>
                </div>

                <div className="pt-1 border-t border-slate-800">
                  <span className="text-[10px] text-slate-400 block">Агрохімічна меліорація:</span>
                  <span className="text-amber-300">{phytoremediationPlan.amendment}</span>
                </div>

                <div className="pt-1 border-t border-slate-800">
                  <span className="text-[10px] text-slate-400 block">Мінеральне живлення:</span>
                  <span className="text-slate-300">{phytoremediationPlan.fertilizer}</span>
                </div>

                <div className="pt-1 border-t border-slate-800">
                  <span className="text-[10px] text-slate-400 block">Екологічний результат:</span>
                  <span className="text-teal-300 font-bold">{phytoremediationPlan.expectedCleaning}</span>
                </div>
              </div>
            </div>

            {/* Remediation Dynamics Curve */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold uppercase text-slate-300 flex items-center gap-1.5">
                  <Activity className="w-4 h-4 text-green-400" />
                  {isUk ? 'Прогноз детоксикації ґрунту (0–36 місяців, % від ГДК)' : 'Soil Detoxification Projection (% MAC)'}
                </span>
                <span className="text-[10px] text-green-400 font-mono">
                  ГДК (MAC) = 20%
                </span>
              </div>

              <div className="h-64 w-full bg-slate-950 rounded-xl p-2 border border-slate-800">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={phytoremediationData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', fontSize: '11px', color: '#fff' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Area type="monotone" dataKey="concRapeseed" name="Ріпак (Brassica napus)" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                    <Area type="monotone" dataKey="concSunflower" name="Соняшник (Helianthus annuus)" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} />
                    <Area type="monotone" dataKey="concWillow" name="Верба (Salix viminalis)" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.1} />
                    <Line type="monotone" dataKey="macLimit" name="Норма ГДК (20%)" stroke="#f43f5e" strokeDasharray="5 5" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: EXPORT REPORT & DAMAGE ACT */}
      {activeTab === 'export_report' && (
        <div className="space-y-6">
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="space-y-1">
                <h2 className="text-sm font-extrabold uppercase text-indigo-400 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-400" />
                  {isUk ? 'Офіційний розрахунковий акт за ДСТУ 4163:2020' : 'Official Damage Assessment Act'}
                </h2>
                <p className="text-xs text-slate-400 font-mono">
                  {isUk ? 'Збереження версії «версія1_2026-09-27» з повним комплексом розрахунків та реквізитами винахідника Банаха А.С.' : 'Standard compliant with Order 167 and DSTU 4163'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleCopyReport}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase rounded-xl transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
                >
                  {isCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  <span>{isCopied ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Копіювати MD' : 'Copy MD')}</span>
                </button>

                <button
                  onClick={handleDownloadMarkdown}
                  className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold uppercase rounded-xl transition-colors flex items-center gap-2 cursor-pointer shadow-md shadow-indigo-900"
                >
                  <Download className="w-4 h-4" />
                  <span>{isUk ? 'Завантажити .MD' : 'Download .MD'}</span>
                </button>

                <button
                  onClick={handleDownloadDocx}
                  disabled={isDownloading}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 text-white text-xs font-extrabold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-lg shadow-emerald-900/40"
                >
                  {isDownloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  <span>{isDownloading ? (isUk ? 'Генерація...' : 'Generating...') : (isUk ? 'Створити Word (.docx)' : 'Generate DOCX')}</span>
                </button>
              </div>
            </div>

            {/* Markdown Preview Box */}
            <div className="bg-slate-950 rounded-xl p-5 border border-slate-800 font-mono text-xs text-slate-300 whitespace-pre-wrap max-h-96 overflow-y-auto leading-relaxed scrollbar-thin scrollbar-thumb-slate-700">
              {markdownReport}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
